/**
 * Correcting a medication order — läkemedel (DECISIONS.md #46).
 *
 * Mirrors `api/src/Continuum.Api/Medications/MedicationEndpoints.cs`'s one route and the codes
 * `MedicationCorrection.Validate` and `MedicationCorrectionService` return.
 *
 * **One route, and no way to create an order.** That asymmetry is #46 point 1 and it is the line
 * this whole feature stops at: a first version of a `clinical.medication_request` *is* an
 * ordination, which is prescribing, and prescribing is out of phase 0 (`PHASE-0.md` §4). Correcting
 * dosing text that was transcribed wrong is documentation. The two are different acts even though
 * they touch the same eight columns, and the record can tell them apart because a correction always
 * has a version to supersede.
 *
 * **Nothing here computes anything** (#46 point 8). The dose is text the clinician typed, the
 * ceiling for a vid behov order is text, and neither is derived from the other — deriving one would
 * be dose calculation, which moves the system towards MDR class IIa (AGENTS.md §3 rule 5).
 */

import { unauthenticated } from '../auth/sessionExpiry'

/**
 * The statuses a correction may set.
 *
 * `entered-in-error` is how an order transcribed against the wrong patient leaves the panel; a
 * `stopped` order is one that was really given and really stopped. Both stay in the record, and the
 * distinction between them is clinically load-bearing rather than bookkeeping.
 */
export const MEDICATION_STATUSES = ['active', 'completed', 'stopped', 'entered-in-error'] as const

export type MedicationStatus = (typeof MEDICATION_STATUSES)[number]

/**
 * What the form sends. Mirrors `MedicationCorrectionRequest`.
 *
 * **Nine fields, and the ones that are not here are the point** (#46 point 3). The substance, the
 * strength as labelled, the dose form, the patient, the encounter, `start_at` and the prescriber are
 * all inherited from the version being corrected. A correction says *this order's dosing was
 * written down wrong*; a correction that could change the substance would be a new order wearing the
 * old one's identity, and nothing downstream — not the audit trail, not a signing list already
 * printed — would show that it had happened.
 */
export interface MedicationCorrectionSubmission {
  /**
   * The `version_id` this correction replaces — the one the panel was showing. Required, never
   * inferred, so a retry after an uncertain outcome is refused rather than writing version 3 that
   * repeats version 2.
   */
  readonly supersedes: string

  /** One of {@link MEDICATION_STATUSES}. */
  readonly status: string

  /**
   * The dose as the clinician typed it, **as text**.
   *
   * A string rather than a number all the way to the server (#46 point 6), because the refusals are
   * about what was typed: `0,25` is `MEDICATION_DOSE_COMMA` and not a repaired `0.25`. A comma as a
   * decimal separator in a medication list is a factor-of-ten risk (AGENTS.md §5), so it is refused
   * with a sentence that says which key to press, and never guessed at.
   *
   * The scale survives: `0.250` is stored as `0.250`, and resending the same number written another
   * way is `MEDICATION_NO_CHANGE` rather than a new version that changed nothing.
   */
  readonly doseAmount: string

  /**
   * The unit — metric, with `mikrogram` written out.
   *
   * **Refused server-side rather than only here**, because `formatDose` *throws* rather than
   * rendering something that could be misread, and this dose appears above the fold on the patient
   * screen. A unit that got past the server would take the panel down. The two blocklists are
   * deliberately the same set: `ClinicalDoseUnit` mirrors `web/src/format/clinicalDose.ts`, so
   * anything the server accepts the formatter will render.
   */
  readonly doseUnit: string

  /** Administration route as documented, free text. Record content. */
  readonly route: string

  /** Dosing interval in the prescriber's words — `1 x 2`. Record content. */
  readonly frequency: string

  /** Vid behov. */
  readonly isPrn: boolean

  /**
   * The 24-hour ceiling for a vid behov order, verbatim and mandatory when {@link isPrn}.
   *
   * Text, never a number, and **never computed** from {@link doseAmount} and {@link frequency}.
   * *Max 4 doser per dygn* is what the prescriber wrote; multiplying two other fields to reach it
   * would be dose calculation.
   */
  readonly prnMaxDose24h: string | null

  /** Indication — ordinationsorsak, mandatory under HSLF-FS 2017:37. Record content. */
  readonly indication: string

  /**
   * When the order ends, as an ISO instant in UTC, or null for an open-ended order.
   *
   * Checked against the **inherited** `start_at`, which is why `MEDICATION_END_BEFORE_START` carries
   * that start back: the form cannot check it alone, because `start_at` is not a field it holds.
   */
  readonly endAt: string | null
}

/** What a successful correction returns. Mirrors `CorrectedMedication`. */
export interface CorrectedMedication {
  /** The order's `record_id`, unchanged — this is the same order. */
  readonly medicationId: string

  /** The new version's `version_id`. What the *next* correction must supersede. */
  readonly versionId: string

  /** 2 for the first correction, then 3, 4 … */
  readonly version: number

  /** The `version_id` that was superseded, echoed back from the row that was written. */
  readonly supersedes: string
}

/**
 * Every code a medication correction can be refused with.
 *
 * Listed rather than left as `string` so a code added on the server without wording here is a
 * typecheck failure rather than a blank message on a form where a clinician is fixing a dose.
 * `medicationRefusalMessage` is exhaustive over this by construction.
 */
export const MEDICATION_REFUSAL_CODES = [
  'MEDICATION_BODY_MISSING',
  'MEDICATION_SUPERSEDES_MISSING',
  'MEDICATION_STATUS_INVALID',
  'MEDICATION_DOSE_MISSING',
  'MEDICATION_DOSE_COMMA',
  'MEDICATION_DOSE_MALFORMED',
  'MEDICATION_DOSE_NOT_POSITIVE',
  'MEDICATION_UNIT_MISSING',
  'MEDICATION_UNIT_NOT_METRIC',
  'MEDICATION_UNIT_MICROGRAM_ABBREVIATED',
  'MEDICATION_FIELD_MISSING',
  'MEDICATION_FIELD_TOO_LONG',
  'MEDICATION_IS_PRN_MISSING',
  'MEDICATION_PRN_MAX_DOSE_MISSING',
  'MEDICATION_END_AT_MALFORMED',
  'MEDICATION_END_BEFORE_START',
  'MEDICATION_NOT_FOUND',
  'MEDICATION_RETRACTED',
  'MEDICATION_VERSION_STALE',
  'MEDICATION_NO_CHANGE',
  'PATIENT_NOT_FOUND',
  'PATIENT_RETRACTED',
  'ROLE_MAY_NOT_DOCUMENT',
] as const

export type MedicationRefusalCode = (typeof MEDICATION_REFUSAL_CODES)[number]

/** Params a refusal can carry. Which are present depends on the code. */
export interface MedicationRefusalParams {
  /** The two field codes: which field, by its submission name, and the limit. */
  readonly field?: string
  readonly max?: number

  /** `MEDICATION_STATUS_INVALID`: what was sent, quoted back. */
  readonly status?: string

  /** `MEDICATION_NOT_FOUND`: the id that did not resolve. */
  readonly medicationId?: string

  /**
   * `MEDICATION_END_BEFORE_START`: the order's `start_at`, inherited and therefore not something the
   * form knew. **Displayed**, unlike every other param here — it is the fact that makes the refusal
   * actionable rather than puzzling.
   */
  readonly startAt?: string

  /** `MEDICATION_VERSION_STALE`: the version that was named. */
  readonly supersedes?: string

  /**
   * `MEDICATION_VERSION_STALE`: the version that is current instead — absent when the race was lost
   * to a concurrent insert. Not displayed either way; the screen refetches.
   */
  readonly current?: string

  /** `PATIENT_NOT_FOUND`: the id that did not resolve. */
  readonly patientId?: string

  /** `ROLE_MAY_NOT_DOCUMENT`: the role that was refused. */
  readonly roleCode?: string
}

/** The server refused the correction, with the code it refused by. */
export class MedicationRefusedError extends Error {
  readonly code: MedicationRefusalCode | 'UNKNOWN'
  readonly params: MedicationRefusalParams
  readonly status: number

  constructor(
    code: MedicationRefusalCode | 'UNKNOWN',
    params: MedicationRefusalParams,
    status: number,
  ) {
    super(`Medication correction refused: ${code}`)
    this.name = 'MedicationRefusedError'
    this.code = code
    this.params = params
    this.status = status
  }
}

/** The API could not be reached, or answered something unusable. */
export class MedicationUnavailableError extends Error {
  constructor() {
    super('Medication correction unavailable')
    this.name = 'MedicationUnavailableError'
  }
}

function isRefusalCode(value: unknown): value is MedicationRefusalCode {
  return (
    typeof value === 'string' && (MEDICATION_REFUSAL_CODES as readonly string[]).includes(value)
  )
}

/**
 * Turns a non-2xx response into the error to throw.
 *
 * An unreadable body still becomes a {@link MedicationRefusedError} with code `UNKNOWN` rather than
 * an unavailable error: the server answered, so nothing was written.
 */
async function refusalFrom(response: Response): Promise<MedicationRefusedError> {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; params?: MedicationRefusalParams }
    }

    return new MedicationRefusedError(
      isRefusalCode(body.error?.code) ? body.error.code : 'UNKNOWN',
      body.error?.params ?? {},
      response.status,
    )
  } catch {
    return new MedicationRefusedError('UNKNOWN', {}, response.status)
  }
}

/**
 * Corrects a medication order: one new `clinical.medication_request` version and its audit row, in
 * one transaction on the server.
 *
 * **What this writes cannot be removed** (AGENTS.md §3). The version on screen stays in the record,
 * which is what makes a corrected dose readable as *this was 0.25 and is now 0.125* rather than as a
 * dose that was always 0.125 — and that difference is the whole reason the record is append-only.
 */
export async function correctMedication(
  patientId: string,
  medicationId: string,
  submission: MedicationCorrectionSubmission,
  signal: AbortSignal,
): Promise<CorrectedMedication> {
  const url =
    `/api/patients/${encodeURIComponent(patientId)}` +
    `/medications/${encodeURIComponent(medicationId)}/versions`

  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(submission),
    })
  } catch (cause) {
    // An abort is the clinician having navigated away mid-request, not a failure. A write that
    // reached the server still happened, which is why the form does not abort a submit in flight.
    if (signal.aborted) {
      throw cause
    }
    throw new MedicationUnavailableError()
  }

  // Before `refusalFrom`, which reads the body: a 401 is the session having ended rather than a
  // refusal of this correction.
  unauthenticated(response)

  if (!response.ok) {
    throw await refusalFrom(response)
  }

  try {
    return (await response.json()) as CorrectedMedication
  } catch {
    // A 201 whose body did not parse: the correction is in the record and this client cannot say
    // which version it became. Unavailable rather than failure, so the clinician reopens the patient
    // and reads the dose the record now holds instead of correcting it a second time.
    throw new MedicationUnavailableError()
  }
}
